// BookTickets/stages/DateAndTime/components/BookingCalendar.tsx
'use client'

import cn from 'classnames'
import Calendar, { type TileArgs } from 'react-calendar'
import type { ReactNode } from 'react'

import styles from './index.module.scss'

import { isSpecialDate } from '../../lib/dateAndTime'
import { getDay, startOfMonth } from 'date-fns'

type BookingCalendarProps = {
  value: Date
  locale: string
  minDate: Date
  maxDate?: Date
  /** Days in the month view that can't be picked, beyond minDate/maxDate. */
  isDateDisabled?: (d: Date) => boolean
  onChange: (d: Date) => void
  onViewChange: (view: string) => void
  onMonthChange: (activeStartDate: Date | null) => void
  prevLabel: ReactNode
  nextLabel: ReactNode
  renderSpecialPopover?: (label: string) => ReactNode
  specialLabel?: string
}

type CalendarStyle = React.CSSProperties & {
  '--calendar-column-offset'?: number | string
}

export default function BookingCalendar({
  value,
  locale,
  minDate,
  maxDate,
  isDateDisabled,
  onChange,
  onViewChange,
  onMonthChange,
  prevLabel,
  nextLabel,
  renderSpecialPopover,
  specialLabel,
}: BookingCalendarProps) {
  const tileClassName = ({ date, view }: TileArgs) => {
    if (view !== 'month') return null
    return isSpecialDate(date) ? [styles.special, 'react-calendar__tile--special'] : null
  }

  const formatDay = (_locale: string | undefined, date: Date) => {
    if (isSpecialDate(date)) return `${date.getDate()}*`
    return `${date.getDate()}`
  }

  const tileContent = ({ date }: { date: Date }) => {
    if (!isSpecialDate(date)) return null
    if (!renderSpecialPopover || !specialLabel) return null
    return renderSpecialPopover(specialLabel)
  }

  const firstDayOfMonthByWeek = () => {
    const day = getDay(startOfMonth(value))
    return day === 0 ? 7 : day
  }

  return (
    <div
      className={cn(styles.calendar)}
      style={{ '--calendar-column-offset': firstDayOfMonthByWeek() } as CalendarStyle}
    >
      <Calendar
        tileContent={tileContent}
        formatDay={formatDay}
        onChange={(d) => onChange(d as Date)}
        onViewChange={({ view }) => onViewChange(view)}
        onActiveStartDateChange={({ view, activeStartDate }) => {
          if (view === 'month') onMonthChange(activeStartDate)
        }}
        value={value}
        showNeighboringMonth={false}
        locale={locale}
        minDate={minDate}
        maxDate={maxDate}
        tileDisabled={({ date, view }) => view === 'month' && Boolean(isDateDisabled?.(date))}
        tileClassName={tileClassName}
        prevLabel={prevLabel}
        nextLabel={nextLabel}
        minDetail="year"
      />
    </div>
  )
}
